'use client';
import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { Bell, Share, X } from 'lucide-react';

/**
 * Pranešimai NE per Android programėlę: iPhone (įsidėjus į pradžios ekraną) ir
 * kompiuterio naršyklė. Android programėlėje nieko nedaro — ten veikia
 * PushRegister (FCM).
 *
 * iPhone Safari pranešimus leidžia TIK atidarius per pradžios ekrano ikoną, o
 * leidimo paklausti galima tik paspaudus mygtuką. Todėl:
 *  • iPhone paprastame Safari — trumpa instrukcija, kaip įsidėti į pradžios ekraną;
 *  • leidimas dar neklaustas — kortelė su mygtuku „Įjungti";
 *  • leidimas jau duotas — tyliai atnaujinam prenumeratą (be jokio lango).
 * Atsisakius kortelė nerodoma 14 dienų.
 */

const ATIDETA_KEY = 'webpush-atideta-iki';
const ATIDETI_MS = 14 * 24 * 60 * 60 * 1000;

type Busena = 'nieko' | 'instrukcija' | 'klausti';

function base64UrlToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function atideta(): boolean {
  try {
    return Number(localStorage.getItem(ATIDETA_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

async function uzsiprenumeruoti(publicKey: string): Promise<boolean> {
  const reg = await navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey),
    });
  }
  const r = await fetch('/api/push/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  return r.ok;
}

export default function WebPushPrompt() {
  const { status } = useSession();
  const [busena, setBusena] = useState<Busena>('nieko');
  const [raktas, setRaktas] = useState<string | null>(null);
  const [dirba, setDirba] = useState(false);
  const [klaida, setKlaida] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') return;
    let atsaukta = false;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) return; // Android programėlė — FCM
      } catch {
        /* ne programėlė */
      }

      const ua = navigator.userAgent;
      const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true;
      const palaiko = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

      if (iOS && !standalone) {
        if (!atideta() && !atsaukta) setBusena('instrukcija');
        return;
      }
      if (!palaiko) return;

      const r = await fetch('/api/push/register');
      if (!r.ok) return;
      const { publicKey } = (await r.json()) as { publicKey: string | null };
      if (!publicKey || atsaukta) return;
      setRaktas(publicKey);

      if (Notification.permission === 'granted') {
        await uzsiprenumeruoti(publicKey).catch(() => undefined);
      } else if (Notification.permission === 'default' && !atideta()) {
        setBusena('klausti');
      }
    })();
    return () => {
      atsaukta = true;
    };
  }, [status]);

  function atideti() {
    try {
      localStorage.setItem(ATIDETA_KEY, String(Date.now() + ATIDETI_MS));
    } catch {
      /* privatus režimas */
    }
    setBusena('nieko');
  }

  async function ijungti() {
    if (!raktas) return;
    setDirba(true);
    setKlaida(null);
    try {
      const leidimas = await Notification.requestPermission();
      if (leidimas !== 'granted') {
        atideti();
        return;
      }
      if (await uzsiprenumeruoti(raktas)) setBusena('nieko');
      else setKlaida('Nepavyko įjungti. Pabandykite dar kartą.');
    } catch {
      setKlaida('Nepavyko įjungti. Pabandykite dar kartą.');
    } finally {
      setDirba(false);
    }
  }

  if (busena === 'nieko') return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] flex justify-center px-3"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-amber-100 p-2 text-amber-700">
            <Bell size={18} />
          </div>
          <div className="min-w-0 flex-1 text-sm text-slate-700">
            {busena === 'instrukcija' ? (
              <>
                <p className="font-semibold text-slate-900">Norite gauti pranešimus į iPhone?</p>
                <ol className="mt-1 list-decimal space-y-0.5 pl-4">
                  <li>
                    Apačioje spauskite <Share size={13} className="inline -mt-0.5" /> <b>Bendrinti</b>
                  </li>
                  <li>
                    Pasirinkite <b>Į pradžios ekraną</b>
                  </li>
                  <li>Atsidarykite sistemą per atsiradusią krano ikoną</li>
                </ol>
              </>
            ) : (
              <>
                <p className="font-semibold text-slate-900">Gauti pranešimus šiame įrenginyje?</p>
                <p className="mt-0.5">Užduotys, priminimai ir kas laukia jūsų sprendimo — net kai sistema uždaryta.</p>
                {klaida && <p className="mt-1 text-rose-600">{klaida}</p>}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => void ijungti()}
                    disabled={dirba}
                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {dirba ? 'Įjungiama…' : 'Įjungti'}
                  </button>
                  <button onClick={atideti} className="rounded-lg border px-3 py-1.5 text-sm">
                    Ne dabar
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={atideti} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Uždaryti">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
